import { useEffect, useState } from 'react'
import { Search, X, CheckCircle } from 'lucide-react'
import { useDispatch } from 'react-redux'
import toast from 'react-hot-toast'
import { api } from '../../services/api'
import { fetchTodayLogs } from '../../store/nutritionSlice'
import { AppDispatch } from '../../store/store'

const MEAL_TYPES = [
  { value: 'BREAKFAST', label: '🌅 Desayuno' },
  { value: 'LUNCH', label: '☀️ Almuerzo' },
  { value: 'DINNER', label: '🌙 Cena' },
  { value: 'SNACK', label: '🍎 Snack' },
]

function AuthFoodImage({ path, className }: { path?: string | null; className: string }) {
  const [src, setSrc] = useState('')
  useEffect(() => {
    if (!path) return undefined
    let url = ''
    let alive = true
    api.get(path, { responseType: 'blob' }).then((r) => {
      if (!alive) return
      url = URL.createObjectURL(r.data)
      setSrc(url)
    }).catch(() => {})
    return () => {
      alive = false
      if (url) URL.revokeObjectURL(url)
    }
  }, [path])
  if (!src) return <div className={`${className} bg-gradient-to-br from-green-50 to-emerald-100`} />
  return <img src={src} alt="" className={className} />
}

export default function LocalMealsPage() {
  const dispatch = useDispatch<AppDispatch>()
  const [rows, setRows] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [query, setQuery] = useState('')
  const [selected, setSelected] = useState<any>(null)
  const [qty, setQty] = useState('1')
  const [mealType, setMealType] = useState('LUNCH')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    api.get('/nutrition/local-meals')
      .then((r) => setRows(r.data || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false))
  }, [])

  const visible = rows.filter((row) => {
    const q = query.trim().toLowerCase()
    if (!q) return true
    return `${row.name || ''} ${row.restaurant || ''}`.toLowerCase().includes(q)
  })

  const openRegister = (row: any) => {
    setSelected(row)
    setQty(String(row.portionQty || 1))
    setMealType('LUNCH')
  }

  const registerMeal = async () => {
    if (!selected || saving) return
    const base = Number(selected.portionQty) || 1
    const next = Number(qty) || base
    const factor = base > 0 ? next / base : 1
    setSaving(true)
    try {
      await api.post('/nutrition/logs', {
        mealType,
        customMealName: selected.restaurant ? `${selected.restaurant} · ${selected.name}` : selected.name,
        itemName: selected.name,
        quantityGrams: next,
        calories: Math.round(Number(selected.calories || 0) * factor * 10) / 10,
        protein: Math.round(Number(selected.protein || 0) * factor * 10) / 10,
        carbs: Math.round(Number(selected.carbs || 0) * factor * 10) / 10,
        fat: Math.round(Number(selected.fat || 0) * factor * 10) / 10,
      })
      dispatch(fetchTodayLogs())
      toast.success(`${selected.name} registrada en tu dashboard`)
      setSelected(null)
    } catch {
      toast.error('No se pudo registrar el plato')
    } finally {
      setSaving(false)
    }
  }

  const scaled = (field: string) => {
    if (!selected) return 0
    const base = Number(selected.portionQty) || 1
    const next = Number(qty) || base
    const factor = base > 0 ? next / base : 1
    return Math.round(Number(selected[field] || 0) * factor)
  }

  return (
    <div className="max-w-4xl mx-auto px-4 pb-12">
      <div className="pt-4 pb-6 flex items-center gap-3">
        <span className="text-4xl">🍽️</span>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Comidas locales</h1>
          <p className="text-sm text-gray-500">Elige un plato y regístralo en tu día</p>
        </div>
      </div>

      <form
        className="flex gap-2 mb-6"
        onSubmit={(e) => { e.preventDefault(); setQuery(search) }}
      >
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar plato..."
            className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-green-400"
          />
        </div>
        <button type="submit" className="px-4 py-2.5 bg-green-500 text-white rounded-xl text-sm font-medium hover:bg-green-600">
          Buscar
        </button>
      </form>

      {loading ? (
        <div className="text-center py-16 text-gray-400">Cargando comidas...</div>
      ) : visible.length === 0 ? (
        <div className="text-center py-16">
          <span className="text-5xl">🍽️</span>
          <p className="text-gray-500 mt-3">No hay comidas locales disponibles aún</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {visible.map((row) => (
            <div key={row.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
              <AuthFoodImage path={row.imageUrl} className="w-full h-40 object-cover" />
              <div className="p-4">
                <h3 className="font-bold text-gray-800 text-sm leading-tight">{row.name}</h3>
                {row.restaurant ? <p className="text-xs text-gray-500 mt-1">{row.restaurant}</p> : null}
                <p className="text-xs text-gray-400 mt-2">{Math.round(Number(row.calories || 0))} kcal</p>
                <button
                  type="button"
                  onClick={() => openRegister(row)}
                  className="mt-3 w-full py-2 bg-green-500 text-white rounded-xl text-sm font-semibold hover:bg-green-600"
                >
                  Registrar
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {selected && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-gray-800">Registrar plato</h3>
              <button type="button" onClick={() => setSelected(null)} className="text-gray-400 hover:text-gray-600">
                <X size={20} />
              </button>
            </div>
            <p className="text-sm text-gray-600 mb-4 font-medium">{selected.name}</p>
            <div className="grid grid-cols-4 gap-2 text-center mb-4">
              {[
                { label: 'Kcal', value: scaled('calories'), color: 'bg-orange-100 text-orange-700' },
                { label: 'Prot', value: `${scaled('protein')}g`, color: 'bg-red-100 text-red-700' },
                { label: 'Carbs', value: `${scaled('carbs')}g`, color: 'bg-yellow-100 text-yellow-700' },
                { label: 'Grasas', value: `${scaled('fat')}g`, color: 'bg-blue-100 text-blue-700' },
              ].map((s) => (
                <div key={s.label} className={`rounded-xl p-2 ${s.color}`}>
                  <p className="font-bold text-sm">{s.value}</p>
                  <p className="text-xs">{s.label}</p>
                </div>
              ))}
            </div>
            <label className="text-sm text-gray-600 font-medium block mb-2">
              Porción ({selected.portionUnit || 'g'})
            </label>
            <input
              value={qty}
              onChange={(e) => setQty(e.target.value)}
              className="w-full mb-4 border border-gray-200 rounded-xl px-3 py-2 text-sm text-gray-900"
            />
            <label className="text-sm text-gray-600 font-medium block mb-2">Comida del día</label>
            <div className="grid grid-cols-2 gap-2 mb-5">
              {MEAL_TYPES.map((m) => (
                <button
                  key={m.value}
                  type="button"
                  onClick={() => setMealType(m.value)}
                  className={`py-2 px-3 rounded-xl text-sm font-medium border-2 ${
                    mealType === m.value
                      ? 'bg-green-500 text-white border-green-500'
                      : 'bg-white text-gray-600 border-gray-200'
                  }`}
                >
                  {m.label}
                </button>
              ))}
            </div>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setSelected(null)}
                className="flex-1 py-2.5 border-2 border-gray-200 rounded-xl text-sm font-medium text-gray-600"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={registerMeal}
                disabled={saving}
                className="flex-1 py-2.5 bg-green-500 text-white rounded-xl text-sm font-semibold hover:bg-green-600 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <CheckCircle size={16} />
                {saving ? 'Registrando...' : 'Registrar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
